import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  DialogContent,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Divider,
  TextField,
  InputAdornment,
  CircularProgress, // Import for loader
} from "@mui/material";

import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Send as SendIcon,
  ArrowDropDown as ArrowDropDownIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import { useLocation, useNavigate } from "react-router-dom";

import FinalLogo from "../../assets/images/Logo_white.png";
import { sidebar } from "../../config/images";

const NotificationDialog = ({ open, onClose }) => {
  const notifications = {
    today: [
      { time: "06:00 PM", text: "your application has accepted in 3 vacancy" },
      { time: "02:27 PM", text: "your interview has seduale in 3 days" },
    ],
    "16-04-2025": [
      { time: "01:00 AM", text: "your premium plan has been expire in 3 days" },
      { time: "04:00 AM", text: "your premium plan has been expire in 3 days" },
      { time: "12:24 PM", text: "your interview has seduale in 3 days" },
      { time: "04:12 AM", text: "your premium plan has been expire in 3 days" },
    ],
    "06-03-2025": [
      { time: "12:45 PM", text: "your interview has seduale in 3 days" },
      { time: "01:20 AM", text: "your premium plan has been expire in 3 days" },
      { time: "01:30 PM", text: "your interview has seduale in 3 days" },
    ],
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle textAlign="center" fontWeight={600}>
        Recent Notifications
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        {Object.entries(notifications).map(([date, items], idx) => (
          <Box key={date}>
            <Typography
              sx={{
                fontWeight: 600,
                px: 2,
                pt: idx === 0 ? 0 : 2,
                fontSize: "14px",
              }}
            >
              {date === "today" ? "Today" : date}
            </Typography>
            <List>
              {items.map((item, i) => (
                <ListItem
                  key={item.time}
                  sx={{ alignItems: "flex-start", py: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 40, marginRight: "11px" }}>
                    {/* <img src={images.recentNotification} alt="" /> */}
                  </ListItemIcon>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <ListItemText
                      key={item.time}
                      primary={
                        <Typography sx={{ fontSize: 14 }}>
                          {item.text}
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "#555", whiteSpace: "nowrap" }}
                    >
                      {item.time}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

// FIX: Added prop validation for NotificationDialog
NotificationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

// Helper component for PremiumDialog features
const PlanFeature = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
    <CheckCircleIcon sx={{ color: "primary.main", mr: 1.5 }} />
    <Typography variant="body2">{children}</Typography>
  </Box>
);

// FIX: Added prop validation for PlanFeature to resolve 'children' is missing error
PlanFeature.propTypes = {
  children: PropTypes.node.isRequired,
};

const PremiumDialog = ({ open, onClose }) => {
  const [plan, setPlan] = useState("monthly");

  const handlePlanChange = (event, newPlan) => {
    if (newPlan !== null) {
      setPlan(newPlan);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: "100%",
            bgcolor: "background.paper",
          },
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <ToggleButtonGroup
            value={plan}
            exclusive
            onChange={handlePlanChange}
            aria-label="subscription plan"
            sx={{
              backgroundColor: "#F2F4F7",
              borderRadius: "20px",
              p: 0.5,
              border: "none",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "16px !important",
                px: 3,
                py: 1,
                textTransform: "none",
                color: "#667085",
                fontWeight: 600,
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                },
                "&:not(.Mui-selected):hover": {
                  backgroundColor: "#E6E8EB",
                },
              },
            }}
          >
            <ToggleButton value="weekly" aria-label="weekly plan">
              Weekly
            </ToggleButton>
            <ToggleButton value="monthly" aria-label="monthly plan">
              Monthly
            </ToggleButton>
            <ToggleButton value="quarterly" aria-label="quarterly plan">
              Quarterly
            </ToggleButton>
            <ToggleButton value="yearly" aria-label="yearly plan">
              Yearly (25% Saved)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid
          container
          spacing={4}
          justifyContent="center"
          alignItems="stretch"
        >
          {/* Basic Plan Card */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent sx={{ p: 4, flexGrow: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  BASIC
                </Typography>
                <Typography variant="h3" fontWeight="bold" sx={{ my: 1 }}>
                  Free
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    my: 2,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: "bold",
                  }}
                >
                  Start Now
                </Button>
                <Typography
                  variant="body2"
                  align="center"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Start your journey now!
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  What's Included:
                </Typography>
                <PlanFeature>Unlimited Resume Templates</PlanFeature>
                <PlanFeature>Unlimited Job Tracking</PlanFeature>
                <PlanFeature>Unlimited Job Autofill</PlanFeature>
                <PlanFeature>One FREE Resume</PlanFeature>
                <PlanFeature>Basic Resume Keyword Matching</PlanFeature>
              </CardContent>
            </Card>
          </Grid>

          {/* Premium Plan Card */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Card
              sx={{
                borderRadius: 3,
                backgroundColor: "primary.main",
                color: "white",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent
                sx={{
                  p: 4,
                  flexGrow: 1,
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
              >
                <Typography variant="overline">PREMIUM PLAN</Typography>
                <Typography variant="h3" fontWeight="bold" sx={{ my: 1 }}>
                  <Box
                    component="span"
                    sx={{
                      textDecoration: "line-through",
                      fontSize: "0.5em",
                      opacity: 0.7,
                      mr: 1,
                    }}
                  >
                    $30
                  </Box>
                  $05
                  <Box
                    component="span"
                    sx={{ fontSize: "0.5em", fontWeight: "normal" }}
                  >
                    /week
                  </Box>
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    my: 2,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: "bold",
                    backgroundColor: "white",
                    color: "primary.main",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                >
                  Start Now
                </Button>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ mb: 3, opacity: 0.9 }}
                >
                  Start your journey now!
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  All Free features, plus:
                </Typography>
                <PlanFeature>Unlimited Resume Templates</PlanFeature>
                <PlanFeature>Unlimited Job Tracking</PlanFeature>
                <PlanFeature>Unlimited Job Autofill</PlanFeature>
                <PlanFeature>One FREE Resume</PlanFeature>
                <PlanFeature>Basic Resume Keyword Matching</PlanFeature>
                <PlanFeature>And more!</PlanFeature>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

// FIX: Added prop validation for PremiumDialog
PremiumDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const AppLayout = ({ active, setActive, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

  // New state for the change password dialog
  const [openChangePasswordDialog, setOpenChangePasswordDialog] =
    useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false); // New loading state

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const toggleDrawer = () => setCollapsed((prev) => !prev);
  const drawerWidth = collapsed ? 80 : 250;

  const handlePremiumModalOpen = () => setIsPremiumModalOpen(true);
  const handlePremiumModalClose = () => setIsPremiumModalOpen(false);
  const handleNotificationModalOpen = () => setIsNotificationModalOpen(true);
  const handleNotificationModalClose = () => setIsNotificationModalOpen(false);
  const handleProfileMenuOpen = (event) =>
    setProfileMenuAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchorEl(null);

  // Handlers for the change password dialog
  const handleOpenChangePasswordDialog = () => {
    setOpenChangePasswordDialog(true);
    handleProfileMenuClose();
  };
  const handleSubmitChangePassword = async () => {};
  const handleCloseChangePasswordDialog = () => {
    setOpenChangePasswordDialog(false);
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  // Handlers for password visibility
  const handleClickShowOldPassword = () => setShowOldPassword((show) => !show);
  const handleMouseDownOldPassword = (event) => {
    event.preventDefault();
  };
  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const handleMouseDownNewPassword = (event) => {
    event.preventDefault();
  };
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);
  const handleMouseDownConfirmPassword = (event) => {
    event.preventDefault();
  };

  const menuItems = [
    { text: "Dashboard", icon: sidebar.dashboard },
    { text: "User Management", icon: sidebar.userManagement },
    { text: "Api Setting", icon: sidebar.apiSettings },
    { text: "Subscriptions", icon: sidebar.subscriptions },
    { text: "Analytics", icon: sidebar.analytics },
    { text: "Settings", icon: sidebar.settings },
    {text: "Privacy Policy", icon: sidebar.privacyPolicy},
    {text: "Terms and Conditions", icon: sidebar.termsAndConditions},
  ];

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Menu List */}
      <List sx={{ paddingTop: "0px" }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ height: 56 }}>
            <ListItemButton
              selected={active === item.text}
              onClick={() => {
                setActive(item.text);
                if (isMobile) {
                  setCollapsed(true);
                }
              }}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "#E6F0FF",
                },
                justifyContent: collapsed ? "center" : "flex-start",
                px: collapsed ? 0 : 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: collapsed ? 0 : 2,
                  ml: collapsed ? 0 : 2,
                }}
              >
                <img src={item.icon} alt={item.text} />
                {!collapsed && (
                  <ListItemText
                    key={item.text}
                    primary={
                      <Typography
                        fontSize={16}
                        fontWeight={600}
                        color={active === item.text ? "#1E76FD" : "#333333"}
                      >
                        {item.text}
                      </Typography>
                    }
                  />
                )}
              </Box>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const navigate = useNavigate();

  const location = useLocation();

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };
  const handleLogout = () => {
    localStorage.clear();
    window.location.reload();
    navigate("/login");
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#1570EF",
          boxShadow: "none",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* Left: Logo + Collapse Button */}
          <Box display="flex" alignItems="center" gap={1}>
            {/* <img src={images.finalLogo} alt="Logo" width={130} /> */}
            <img src={FinalLogo} alt="Logo" width={130} />
            <IconButton
              color="inherit"
              onClick={toggleDrawer}
              style={{ marginLeft: "61px" }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Box>

          {/* Right: Buttons */}
          {isMobile ? (
            <>
              <IconButton color="inherit" onClick={handleMenuOpen}>
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {/* <MenuItem onClick={() => navigate("/dashboard/profile")}>
                  Profile
                </MenuItem> */}
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    handleOpenChangePasswordDialog();
                  }}
                >
                  Change Password
                </MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </>
          ) : (
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ cursor: "pointer" }}
                onClick={handleProfileMenuOpen}
              >
                <Avatar sx={{ width: 32, height: 32, fontSize: "12px" }}>
                  {/* {getInitials(userName)} */}
                </Avatar>
                <Typography sx={{ color: "white", fontWeight: 500 }}>
                  {/* {userName} */}
                </Typography>
                <ArrowDropDownIcon sx={{ color: "white" }} />
              </Box>
              <Menu
                anchorEl={profileMenuAnchorEl}
                open={Boolean(profileMenuAnchorEl)}
                onClose={handleProfileMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  "& .MuiMenuItem-root": {
                    paddingTop: "20px",
                    padding: "10px 24px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#333333",
                    "&:hover": { backgroundColor: "#E6F0FF" },
                  },
                }}
                className="profile-menu"
              >
                {/* <MenuItem onClick={() => navigate("/dashboard/profile")}>
                  Profile
                </MenuItem> */}
                <MenuItem onClick={handleOpenChangePasswordDialog}>
                  Change Password
                </MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={!isMobile || !collapsed}
        onClose={() => setCollapsed(true)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            top: { xs: "57px", sm: "64px", md: "64px" },
            height: { xs: "calc(100% - 57px)", md: "calc(100% - 64px)" },
            boxSizing: "border-box",
            border: "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            transition: "width 0.3s ease",
            overflowX: "hidden",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        sx={{
          marginLeft: isMobile ? 0 : `${drawerWidth}px`,
          marginTop: "57px",
        }}
      >
        {children}
      </Box>

      <PremiumDialog
        open={isPremiumModalOpen}
        onClose={handlePremiumModalClose}
      />
      <NotificationDialog
        open={isNotificationModalOpen}
        onClose={handleNotificationModalClose}
      />

      {/* Change Password Dialog */}
      <Dialog
        open={openChangePasswordDialog}
        onClose={handleCloseChangePasswordDialog}
        aria-labelledby="change-password-dialog-title"
      >
        <DialogTitle id="change-password-dialog-title">
          Change Password
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter your old password and new password.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="old-password"
            label="Old Password"
            type={showOldPassword ? "text" : "password"}
            fullWidth
            variant="standard"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle old password visibility"
                    onClick={handleClickShowOldPassword}
                    onMouseDown={handleMouseDownOldPassword}
                    edge="end"
                  >
                    {showOldPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            id="new-password"
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            variant="standard"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle new password visibility"
                    onClick={handleClickShowNewPassword}
                    onMouseDown={handleMouseDownNewPassword}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            id="confirm-password"
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            variant="standard"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={handleClickShowConfirmPassword}
                    onMouseDown={handleMouseDownConfirmPassword}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseChangePasswordDialog}
            disabled={loading}
            sx={{
              bgcolor: "#f4f4f4",
              color: "#333",
              "&:hover": { bgcolor: "#e0e0e0" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitChangePassword}
            disabled={loading}
            variant="contained"
            sx={{
              bgcolor: "#1E76FD",
              color: "white",
              "&:hover": { bgcolor: "#1964d1" },
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={24} color="inherit" />
                Submitting...
              </Box>
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppLayout;

// FIX: Corrected prop validation for AppLayout
AppLayout.propTypes = {
  active: PropTypes.string,
  setActive: PropTypes.func,
  children: PropTypes.node,
};
